import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomIcon from "../../../components/CustomIcon.tsx";
import ProgressBar from "../../../components/ProgressBar.tsx";
import ProgressCircle from "../../../components/ProgressCircle.tsx";
import { useJournalContext } from "../../../contexts/journal.tsx";
import { useUsersContext } from "../../../contexts/users.tsx";
import { Color } from "../../../models/helpers/colors.ts";
import formatNumber from "../../../models/helpers/formatNumber.ts";
import { JournalSummary } from "../../../models/journals/journalSummary.ts";

const JournalDayProgress = ({
  selectedDate
}: {
  selectedDate: Date;
}) => {
  const navigate = useNavigate();
  const { profile } = useUsersContext();
  const { days } = useJournalContext();
  const [selectedDay, setSelectedDay] = useState<JournalSummary>(days.find((day) => day.dateTime.toISOString().split('T')[0] == selectedDate.toISOString().split('T')[0]) ?? {} as JournalSummary);

  useEffect(() => {
    setSelectedDay(days.find((day) => day.dateTime == selectedDate) ?? {} as JournalSummary);
  }, [days, selectedDate]);

  const getUserGoalCalories = () => {
    if (selectedDay.userGoalCalories != null) {
      return formatNumber.ToFixed(selectedDay.userGoalCalories, 0);
    }
    // Find the most recent previous day with userGoalCalories
    const prevDay = [...days]
      .filter((day) => day.dateTime < selectedDate && day.userGoalCalories != null)
      .sort((a, b) => b.dateTime.getTime() - a.dateTime.getTime())[0];

    if (prevDay != null) {
      return formatNumber.ToFixed(prevDay.userGoalCalories, 0);
    }

    return formatNumber.ToFixed(profile.userGoals?.dailyCalorieTarget ?? 0, 0);
  }

  const getConsumedCalories = () => {
    return formatNumber.ToFixed(selectedDay.calories, 0);
  }

  const getRemainingCaloriesNumber = () => {
    return (profile.userGoals?.dailyCalorieTarget ?? 0) - (selectedDay.calories ?? 0);
  }

  const getRemainingCalories = () => {
    var number = Math.abs(getRemainingCaloriesNumber());
    return formatNumber.ToFixed(number, 0);
  }

  const getProtein = () => {
    return formatNumber.ToFixed(selectedDay.protein, 0);
  }

  const getProteinPercent = () => {
    return selectedDay.protein * 100 / (profile.userGoals?.dailyProteinGramsTarget ?? 0);
  }

  const getCarbohydrates = () => {
    return formatNumber.ToFixed(selectedDay.carbohydrates, 0);
  }

  const getCarbohydratesPercent = () => {
    return selectedDay.carbohydrates * 100 / (profile.userGoals?.dailyCarbohydratesGramsTarget ?? 0);
  }

  const getTotalFat = () => {
    return formatNumber.ToFixed(selectedDay.totalFat, 0);
  }

  const getTotalFatPercent = () => {
    return selectedDay.totalFat * 100 / (profile.userGoals?.dailyTotalFatGramsTarget ?? 0);
  }

  if (profile.userGoals == null || profile.userGoals.dailyProteinTarget == null || profile.userGoals.dailyCarbohydratesTarget == null || profile.userGoals.dailyTotalFatTarget == null)
    return null;

  return (
    <div className="d-flex flex-column align-items-center pb-2">
      <div className="card w-100">
        <div className="card-body">
          <div className="container">
            <div className="col">
              <div className="row">
                <div className="col">
                  <div className="row">
                    <h5 className="card-title">Goals</h5>
                    <p>{getUserGoalCalories()} kCal</p>
                  </div>
                  <div className="row">
                    <h6 className="card-title">Consumed</h6>
                    <p>{getConsumedCalories()} kCal</p>
                  </div>
                </div>
                <div className="col" style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '0', right: '0' }} onClick={() => navigate('/journal/statistics')}>
                    <CustomIcon name='graph' />
                  </div>
                  <div className="d-flex flex-column align-items-center">
                    <div className="mt-3">
                      <ProgressCircle percent={selectedDay.percent ?? 0} text={`${getRemainingCalories()}kCal`} radius={50} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col w-30">
                  <ProgressBar
                    percent={getProteinPercent()}
                    label="Protein"
                    smallText={`${getProtein()} / ${profile.userGoals?.dailyProteinGramsTarget}g`}
                  />
                </div>
                <div className="col w-30">
                  <ProgressBar
                    percent={getCarbohydratesPercent()}
                    label="Carbs"
                    smallText={`${getCarbohydrates()} / ${profile.userGoals?.dailyCarbohydratesGramsTarget}g`}
                    color={Color.warning}
                  />
                </div>
                <div className="col w-30">
                  <ProgressBar
                    percent={getTotalFatPercent()}
                    label="Fats"
                    smallText={`${getTotalFat()} / ${profile.userGoals?.dailyTotalFatGramsTarget}g`}
                    color={Color.danger}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JournalDayProgress;